import React, { useEffect, useRef, useState } from "react";
import * as echarts from "echarts";
import {
    styled,
    Table,
    TableBody,
    TableCell,
    tableCellClasses,
    TableContainer,
    TableHead,
    TableRow,
    Paper,
    Box,
    Typography,
    CircularProgress,
    Alert,
} from "@mui/material";
import { getAverageCustomerCount } from "../../api/api";
import { getCurrentUser } from "../../utils/auth";

// Styled Table Cells and Rows
const StyledTableCell = styled(TableCell)(({ theme }) => ({
    [`&.${tableCellClasses.head}`]: {
        backgroundColor: theme.palette.primary.main,
        color: theme.palette.common.white,
        fontWeight: "bold",
    },
    [`&.${tableCellClasses.body}`]: {
        fontSize: 14,
        textAlign: "center",
    },
}));

const StyledTableRow = styled(TableRow)(({ theme }) => ({
    "&:nth-of-type(odd)": {
        backgroundColor: theme.palette.action.hover,
    },
}));

const Customer_Day_Report = ({ startDate, endDate }) => {
    const chartRef = useRef(null);
    const [data, setData] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [currentUser, setCurrentUser] = useState(null);

    useEffect(() => {
        const fetchData = async () => {
            try {
                setLoading(true);
                const user = getCurrentUser();
                setCurrentUser(user);

                const startDateParam = startDate ? startDate.format("YYYY-MM-DD") : undefined;
                const endDateParam = endDate ? endDate.format("YYYY-MM-DD") : undefined;
                const response = await getAverageCustomerCount(startDateParam, endDateParam);
                console.log("Customer Day Report API Response:", response);
                if (response.success) {
                    console.log("Customer Day Report Data:", response.data);
                    setData(response.data);
                } else {
                    setError(response.message || "Failed to fetch data");
                }
            } catch (err) {
                console.error("Error fetching customer count data:", err);
                setError("Failed to load customer count data");
            } finally {
                setLoading(false);
            }
        };

        fetchData();
    }, [startDate, endDate]);

    useEffect(() => {
        if (!data || !chartRef.current) return;

        const chartInstance = echarts.init(chartRef.current);

        const categories = data.map((item) => item.day);
        const values = data.map((item) => parseInt(item.customer_count) || 0);

        const options = {
            title: {
                text: `Customer Count per Day${
                    currentUser?.branch_name
                        ? ` - ${currentUser.branch_name}`
                        : ""
                }`,
                left: "center",
                textStyle: { fontSize: 16, fontWeight: "bold" },
            },
            tooltip: {
                trigger: "axis",
                formatter: function (params) {
                    const data = params[0];
                    return `${data.name}<br/>Customers: ${data.value}`;
                },
            },
            xAxis: {
                type: "category",
                data: categories,
                axisLabel: { rotate: 30 },
            },
            yAxis: {
                type: "value",
                name: "Customers",
            },
            series: [
                {
                    data: values,
                    type: "bar",
                    barWidth: "50%",
                    itemStyle: {
                        color: "#1976d2",
                    },
                    label: {
                        show: true,
                        position: "top",
                        formatter: "{c}",
                    },
                },
            ],
        };

        chartInstance.setOption(options);

        // Responsive resize
        const handleResize = () => chartInstance.resize();
        window.addEventListener("resize", handleResize);

        return () => {
            chartInstance.dispose();
            window.removeEventListener("resize", handleResize);
        };
    }, [data]);

    if (loading) {
        return (
            <Box sx={{ display: "flex", justifyContent: "center", my: 4 }}>
                <CircularProgress />
            </Box>
        );
    }

    if (error) {
        return (
            <Box sx={{ p: 3 }}>
                <Alert severity="error">{error}</Alert>
            </Box>
        );
    }

    return (
        <Box sx={{ p: 3 }}>
            {/* Summary Stats */}
            <Box
                sx={{
                    mb: 3,
                    p: 2,
                    bgcolor: "background.paper",
                    borderRadius: 1,
                }}
            >
                <Typography variant="h6" gutterBottom>
                    Summary Statistics
                    {currentUser?.branch_name
                        ? ` - ${currentUser.branch_name}`
                        : ""}
                </Typography>
                <Box sx={{ display: "flex", gap: 3, flexWrap: "wrap" }}>
                    <Box>
                        <Typography variant="body2" color="text.secondary">
                            Total Customers
                        </Typography>
                        <Typography variant="h6">
                            {data && Array.isArray(data) && data.length > 0
                                ? data.reduce((sum, item) => {
                                    const count = parseInt(item.customer_count) || 0;
                                    return sum + count;
                                }, 0)
                                : 0}
                        </Typography>
                    </Box>
                    <Box>
                        <Typography variant="body2" color="text.secondary">
                            Average per Day
                        </Typography>
                        <Typography variant="h6">
                            {data && Array.isArray(data) && data.length > 0
                                ? Math.round(
                                    data.reduce((sum, item) => {
                                        const count = parseInt(item.customer_count) || 0;
                                        return sum + count;
                                    }, 0) / data.length
                                )
                                : 0}
                        </Typography>
                    </Box>
                    <Box>
                        <Typography variant="body2" color="text.secondary">
                            Peak Day
                        </Typography>
                        <Typography variant="h6">
                            {data && Array.isArray(data) && data.length > 0
                                ? data.reduce((max, item) => {
                                    const currentCount = parseInt(item.customer_count) || 0;
                                    const maxCount = parseInt(max.customer_count) || 0;
                                    return currentCount > maxCount ? item : max;
                                }).day
                                : "N/A"}
                        </Typography>
                    </Box>
                </Box>
            </Box>

            {/* Table */}
            <TableContainer component={Paper} sx={{ mb: 4 }}>
                <Table
                    sx={{ minWidth: 500 }}
                    aria-label="Customer Day Report Table"
                >
                    <TableHead>
                        <TableRow>
                            <StyledTableCell>Day</StyledTableCell>
                            <StyledTableCell>Date</StyledTableCell>
                            <StyledTableCell>Customer Count</StyledTableCell>
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        {data?.map((row) => (
                            <StyledTableRow key={row.date}>
                                <StyledTableCell>{row.day}</StyledTableCell>
                                <StyledTableCell>{row.date}</StyledTableCell>
                                <StyledTableCell>
                                    {parseInt(row.customer_count) || 0}
                                </StyledTableCell>
                            </StyledTableRow>
                        ))}
                    </TableBody>
                </Table>
            </TableContainer>

            {/* Chart */}
            <Typography variant="h6" align="center" sx={{ mb: 2 }}>
                Customer Count per Day (Bar Chart)
            </Typography>
            <Box
                ref={chartRef}
                sx={{
                    width: "100%",
                    height: 400,
                    borderRadius: 2,
                    backgroundColor: "#fff",
                    boxShadow: 2,
                }}
            />
        </Box>
    );
};

export default Customer_Day_Report;
